import React, { useState, useEffect, useCallback } from 'react';
import { 
  Wallet, Plus, Shield, ArrowUpRight, ArrowDownLeft, AlertTriangle,
  Building2, CreditCard, Trash2, Star, ChevronDown, ChevronUp,
  CheckCircle2, XCircle, Clock, FileText, Lock, Eye, EyeOff,
  MessageCircle, Send
} from 'lucide-react';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { EmptyState } from '../components/EmptyState';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const statusConfig = {
  held: { label: 'Retenido', color: 'bg-amber-500/10 text-amber-600 border-amber-500/30', icon: Clock },
  released: { label: 'Liberado', color: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30', icon: CheckCircle2 },
  refunded: { label: 'Reembolsado', color: 'bg-brand-green/10 text-brand-deep dark:text-brand-green border-brand-green/30', icon: ArrowDownLeft },
  disputed: { label: 'En Disputa', color: 'bg-red-500/10 text-red-600 border-red-500/30', icon: AlertTriangle },
  cancelled: { label: 'Cancelado', color: 'bg-gray-500/10 text-gray-500 border-gray-500/30', icon: XCircle },
};

const EscrowPayments = () => {
  const { user, isAdmin } = useAuth();
  const { customers, products } = useApp();
  const [tab, setTab] = useState('transactions');
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [showNewTransaction, setShowNewTransaction] = useState(false);
  const [showNewBank, setShowNewBank] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({ bank_account_id: '', amount: '' });
  const [policy, setPolicy] = useState(null);
  const [expandedTx, setExpandedTx] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  const [showAccountNumbers, setShowAccountNumbers] = useState({});
  const [chatMessages, setChatMessages] = useState({});
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);

  const [txForm, setTxForm] = useState({
    customer_id: '', product_id: '', product_name: '', amount: '', currency: 'usd'
  });
  const [bankForm, setBankForm] = useState({
    bank_name: '', account_holder: '', account_number: '', account_type: 'checking',
    routing_number: '', currency: 'usd', is_primary: false
  });

  const fetchData = useCallback(async () => {
    try {
      const [balRes, txRes, bankRes, wdRes] = await Promise.all([
        axios.get(`${API}/escrow/balance`),
        axios.get(`${API}/escrow/transactions${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`),
        axios.get(`${API}/escrow/bank-accounts`),
        axios.get(`${API}/escrow/withdrawals`)
      ]);
      setBalance(balRes.data);
      setTransactions(txRes.data);
      setBankAccounts(bankRes.data);
      setWithdrawals(wdRes.data);
    } catch (err) {
      // Error handled by toast
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateTransaction = async () => {
    if (!txForm.customer_id || !txForm.product_name || !txForm.amount) return;
    try {
      await axios.post(`${API}/escrow/create`, {
        customer_id: txForm.customer_id,
        product_id: txForm.product_id || null,
        product_name: txForm.product_name,
        amount: parseFloat(txForm.amount),
        currency: txForm.currency,
        origin_url: window.location.origin
      });
      toast.success('Transacción escrow creada exitosamente');
      setShowNewTransaction(false);
      setTxForm({ customer_id: '', product_id: '', product_name: '', amount: '', currency: 'usd' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear transacción');
    }
  };

  const handleCreateBank = async () => {
    if (!bankForm.bank_name || !bankForm.account_holder || !bankForm.account_number) return;
    try {
      await axios.post(`${API}/escrow/bank-accounts`, bankForm);
      toast.success('Cuenta bancaria agregada');
      setShowNewBank(false);
      setBankForm({ bank_name: '', account_holder: '', account_number: '', account_type: 'checking', routing_number: '', currency: 'usd', is_primary: false });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al agregar cuenta');
    }
  };

  const handleDeleteBank = async (id) => {
    if (!window.confirm('¿Eliminar esta cuenta bancaria?')) return;
    try {
      await axios.delete(`${API}/escrow/bank-accounts/${id}`);
      toast.success('Cuenta eliminada');
      fetchData();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const handleSetPrimary = async (id) => {
    try {
      await axios.put(`${API}/escrow/bank-accounts/${id}/primary`);
      toast.success('Cuenta principal actualizada');
      fetchData();
    } catch (err) {
      toast.error('Error al actualizar');
    }
  };

  const handleAction = async (txId, action, reason = '') => {
    setActionLoading(txId);
    try {
      await axios.post(`${API}/escrow/action`, { transaction_id: txId, action, reason });
      const labels = { release: 'Fondos liberados', refund: 'Reembolso procesado', dispute: 'Disputa abierta' };
      toast.success(labels[action] || 'Acción completada');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al procesar acción');
    } finally {
      setActionLoading(null);
    }
  };

  const loadPolicy = async () => {
    try {
      const res = await axios.get(`${API}/escrow/privacy-policy`);
      setPolicy(res.data);
      setShowPolicy(true);
    } catch (err) { console.warn('Policy load error:', err?.message); toast.error('Error al cargar política'); }
  };

  const loadChat = async (txId) => {
    try {
      const res = await axios.get(`${API}/escrow/chat/${txId}`);
      setChatMessages(prev => ({ ...prev, [txId]: res.data }));
    } catch (err) { console.warn('Chat load error:', err?.message); toast.error('Error al cargar chat'); }
  };

  const sendChatMessage = async (txId) => {
    if (!chatInput.trim()) return;
    setChatLoading(true);
    try {
      const res = await axios.post(`${API}/escrow/chat`, { transaction_id: txId, message: chatInput });
      setChatMessages(prev => ({ ...prev, [txId]: [...(prev[txId] || []), res.data] }));
      setChatInput('');
    } catch (err) { console.warn('Chat send error:', err?.message); toast.error('Error al enviar mensaje'); }
    finally { setChatLoading(false); }
  };

  const handleWithdraw = async () => {
    if (!withdrawForm.bank_account_id || !withdrawForm.amount) return;
    try {
      await axios.post(`${API}/escrow/withdraw`, {
        bank_account_id: withdrawForm.bank_account_id,
        amount: parseFloat(withdrawForm.amount),
        currency: 'usd'
      });
      toast.success('Retiro solicitado. Se procesará en 3 días hábiles.');
      setShowWithdraw(false);
      setWithdrawForm({ bank_account_id: '', amount: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al solicitar retiro');
    }
  };

  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setTxForm(prev => ({
        ...prev,
        product_id: productId,
        product_name: product.name,
        amount: product.price ? String(product.price) : prev.amount
      }));
    }
  };

  const fmt = (n) => n != null ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '$0.00';

  return (
    <div data-testid="escrow-page" className="min-h-screen">
      <Header title="Pagos Seguros" subtitle="Sistema Escrow - Protección de compras">
        <Button data-testid="view-policy-btn" variant="outline" size="sm" className="gap-2" onClick={loadPolicy}>
          <FileText className="w-4 h-4" /> Políticas
        </Button>
        {balance && balance.available_to_withdraw > 0 && (
          <Button data-testid="withdraw-btn" variant="outline" size="sm" className="gap-2 text-emerald-700 dark:text-emerald-400 border-emerald-500/30" onClick={() => setShowWithdraw(true)}>
            <ArrowUpRight className="w-4 h-4" /> Retirar {fmt(balance.available_to_withdraw)}
          </Button>
        )}
        <Button data-testid="new-escrow-btn" className="gap-2 bg-primary hover:opacity-90" onClick={() => setShowNewTransaction(true)}>
          <Plus className="w-4 h-4" /> Nueva Transacción
        </Button>
      </Header>

      <div className="p-6 space-y-6 max-w-6xl">
        {/* Balance Cards */}
        {balance && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="glass-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                  </div>
                  <span className="text-xs text-muted-foreground">Fondos Retenidos</span>
                </div>
                <p data-testid="held-balance" className="text-xl font-bold text-amber-700 dark:text-amber-400">{fmt(balance.held_balance)}</p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                  </div>
                  <span className="text-xs text-muted-foreground">Disponible</span>
                </div>
                <p data-testid="available-balance" className="text-xl font-bold text-emerald-700 dark:text-emerald-400">{fmt(balance.available_to_withdraw)}</p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-brand-green/10 flex items-center justify-center">
                    <ArrowDownLeft className="w-4 h-4 text-brand-deep dark:text-brand-green" />
                  </div>
                  <span className="text-xs text-muted-foreground">Reembolsado</span>
                </div>
                <p className="text-xl font-bold text-brand-deep dark:text-brand-green">{fmt(balance.refunded_total)}</p>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
                    <Wallet className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green" />
                  </div>
                  <span className="text-xs text-muted-foreground">Total Transacciones</span>
                </div>
                <p className="text-xl font-bold">{balance.total_transactions}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Security Notice */}
        <Card className="border-emerald-500/20 bg-emerald-500/5">
          <CardContent className="p-4 flex items-center gap-3">
            <Lock className="w-5 h-5 text-emerald-700 dark:text-emerald-400 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-700 dark:text-emerald-400">Protección Escrow Activa</p>
              <p className="text-xs text-muted-foreground">Los fondos se retienen de forma segura hasta confirmar la entrega. Comisión: 2.5% por transacción liberada.</p>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="transactions" data-testid="tab-transactions">Transacciones</TabsTrigger>
            <TabsTrigger value="bank-accounts" data-testid="tab-bank-accounts">Cuentas Bancarias</TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="tab-withdrawals">Retiros ({withdrawals.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="transactions" className="mt-4 space-y-4">
            {/* Filters */}
            <div className="flex items-center gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter" className="w-40">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="held">Retenidos</SelectItem>
                  <SelectItem value="released">Liberados</SelectItem>
                  <SelectItem value="refunded">Reembolsados</SelectItem>
                  <SelectItem value="disputed">En Disputa</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">{transactions.length} transacciones</span>
            </div>

            {/* Transaction List */}
            <AnimatePresence>
              {transactions.map((tx) => {
                const cfg = statusConfig[tx.status] || statusConfig.held;
                const StatusIcon = cfg.icon;
                const isExpanded = expandedTx === tx.id;

                return (
                  <motion.div
                    key={tx.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                  >
                    <Card data-testid={`escrow-tx-${tx.id}`} className="glass-card overflow-hidden">
                      <CardContent className="p-0">
                        <div
                          className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                          onClick={() => setExpandedTx(isExpanded ? null : tx.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", cfg.color)}>
                                <StatusIcon className="w-5 h-5" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-sm">{tx.product_name}</span>
                                  <Badge variant="outline" className={cn("text-xs", cfg.color)}>{cfg.label}</Badge>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {tx.buyer_name} &middot; {new Date(tx.created_at).toLocaleDateString('es')}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <p className="font-bold">{fmt(tx.amount)}</p>
                                <p className="text-xs text-muted-foreground">{tx.currency?.toUpperCase()}</p>
                              </div>
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </div>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="border-t border-border p-4 space-y-4 bg-muted/10">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <p className="text-xs text-muted-foreground">Comprador</p>
                                <p className="font-medium">{tx.buyer_name}</p>
                                {tx.buyer_email && <p className="text-xs text-muted-foreground">{tx.buyer_email}</p>}
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Monto</p>
                                <p className="font-medium">{fmt(tx.amount)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Comisión (2.5%)</p>
                                <p className="font-medium">{fmt(tx.fee)}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground">Neto a recibir</p>
                                <p className="font-medium text-emerald-700 dark:text-emerald-400">{fmt(tx.amount - tx.fee)}</p>
                              </div>
                            </div>
                            {tx.notes && (
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Notas</p>
                                <p className="text-sm whitespace-pre-line">{tx.notes}</p>
                              </div>
                            )}
                            {tx.dispute_reason && (
                              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                                <p className="text-xs font-medium text-red-600 dark:text-red-400">Razón de disputa</p>
                                <p className="text-sm">{tx.dispute_reason}</p>
                              </div>
                            )}

                            {/* Escrow Chat */}
                            <div className="border rounded-lg overflow-hidden">
                              <button
                                className="w-full flex items-center gap-2 p-3 text-xs font-medium text-muted-foreground hover:bg-muted/30 transition-colors"
                                onClick={(e) => { e.stopPropagation(); loadChat(tx.id); }}
                              >
                                <MessageCircle className="w-3.5 h-3.5" />
                                Chat de la transacción ({chatMessages[tx.id]?.length || 0} mensajes)
                              </button>
                              {chatMessages[tx.id] && (
                                <div className="border-t border-border">
                                  <ScrollArea className="max-h-40 p-3">
                                    {chatMessages[tx.id].length === 0 && (
                                      <p className="text-xs text-muted-foreground text-center py-2">Sin mensajes aún</p>
                                    )}
                                    {chatMessages[tx.id].map(msg => (
                                      <div key={msg.id} className="mb-2 last:mb-0">
                                        <div className="flex items-center gap-1.5 mb-0.5">
                                          <span className="text-xs font-medium">{msg.sender_name}</span>
                                          <Badge variant="outline" className="text-[9px] px-1 py-0">{msg.sender_role}</Badge>
                                          <span className="text-[10px] text-muted-foreground">{new Date(msg.created_at).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}</span>
                                        </div>
                                        <p className="text-sm pl-0">{msg.message}</p>
                                      </div>
                                    ))}
                                  </ScrollArea>
                                  <div className="flex items-center gap-2 p-2 border-t border-border">
                                    <Input
                                      data-testid={`escrow-chat-input-${tx.id}`}
                                      value={chatInput}
                                      onChange={e => setChatInput(e.target.value)}
                                      onKeyDown={e => e.key === 'Enter' && sendChatMessage(tx.id)}
                                      placeholder="Escribe un mensaje..."
                                      className="h-8 text-sm"
                                      onClick={e => e.stopPropagation()}
                                    />
                                    <Button
                                      size="icon"
                                      className="h-8 w-8 bg-violet-500 hover:bg-violet-600"
                                      onClick={(e) => { e.stopPropagation(); sendChatMessage(tx.id); }}
                                      disabled={chatLoading || !chatInput.trim()}
                                    >
                                      <Send className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* Actions */}
                            {tx.status === 'held' && (
                              <div className="flex gap-2 pt-2">
                                <Button
                                  data-testid={`release-btn-${tx.id}`}
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                                  onClick={(e) => { e.stopPropagation(); handleAction(tx.id, 'release', 'Entrega confirmada'); }}
                                  disabled={actionLoading === tx.id}
                                >
                                  <CheckCircle2 className="w-3 h-3" /> Confirmar Entrega y Liberar
                                </Button>
                                <Button
                                  data-testid={`dispute-btn-${tx.id}`}
                                  variant="outline"
                                  size="sm"
                                  className="text-amber-600 border-amber-500/30 gap-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const reason = window.prompt('Razón de la disputa:');
                                    if (reason) handleAction(tx.id, 'dispute', reason);
                                  }}
                                  disabled={actionLoading === tx.id}
                                >
                                  <AlertTriangle className="w-3 h-3" /> Abrir Disputa
                                </Button>
                                <Button
                                  data-testid={`refund-btn-${tx.id}`}
                                  variant="destructive"
                                  size="sm"
                                  className="gap-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm('¿Reembolsar al comprador?')) handleAction(tx.id, 'refund', 'Reembolso solicitado');
                                  }}
                                  disabled={actionLoading === tx.id}
                                >
                                  <ArrowDownLeft className="w-3 h-3" /> Reembolsar
                                </Button>
                              </div>
                            )}
                            {tx.status === 'disputed' && (
                              <div className="flex gap-2 pt-2">
                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                                  onClick={(e) => { e.stopPropagation(); handleAction(tx.id, 'release', 'Disputa resuelta a favor del vendedor'); }}
                                  disabled={actionLoading === tx.id}>
                                  <CheckCircle2 className="w-3 h-3" /> Resolver y Liberar
                                </Button>
                                <Button variant="destructive" size="sm" className="gap-1"
                                  onClick={(e) => { e.stopPropagation(); handleAction(tx.id, 'refund', 'Disputa resuelta a favor del comprador'); }}
                                  disabled={actionLoading === tx.id}>
                                  <ArrowDownLeft className="w-3 h-3" /> Resolver y Reembolsar
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {transactions.length === 0 && (
              <Card className="glass-card border-dashed">
                <CardContent className="py-12 text-center">
                  <EmptyState
                    mood="empty"
                    title="No hay transacciones escrow"
                    description="Las ventas con pago protegido aparecerán aquí hasta que el cliente confirme la entrega."
                    action={(
                      <Button variant="outline" className="gap-2" onClick={() => setShowNewTransaction(true)}>
                        <Plus className="w-4 h-4" /> Crear primera transacción
                      </Button>
                    )}
                    testId="escrow-empty"
                  />
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="bank-accounts" className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{bankAccounts.length} cuentas registradas</p>
              <Button data-testid="add-bank-btn" variant="outline" className="gap-2" onClick={() => setShowNewBank(true)}>
                <Building2 className="w-4 h-4" /> Agregar Cuenta
              </Button>
            </div>

            {bankAccounts.map((acc) => (
              <Card key={acc.id} data-testid={`bank-${acc.id}`} className="glass-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary dark:from-slate-800 dark:to-slate-700 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-slate-600 dark:text-slate-300" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{acc.bank_name}</span>
                          {acc.is_primary && <Badge className="bg-violet-500/10 text-brand-deep dark:text-brand-deep dark:text-brand-green border-violet-500/30 text-xs">Principal</Badge>}
                          {acc.is_verified && <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 text-xs">Verificada</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">{acc.account_holder}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <CreditCard className="w-3 h-3 text-muted-foreground" />
                          <span className="text-xs font-mono text-muted-foreground">
                            {showAccountNumbers[acc.id] ? acc.account_number_masked : '••••••••'}
                          </span>
                          <button onClick={() => setShowAccountNumbers(prev => ({...prev, [acc.id]: !prev[acc.id]}))} className="text-muted-foreground hover:text-foreground">
                            {showAccountNumbers[acc.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!acc.is_primary && (
                        <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={() => handleSetPrimary(acc.id)}>
                          <Star className="w-3 h-3" /> Principal
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDeleteBank(acc.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {bankAccounts.length === 0 && (
              <Card className="glass-card border-dashed">
                <CardContent className="py-12 text-center">
                  <Building2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground/20" />
                  <p className="text-muted-foreground">No tienes cuentas bancarias registradas</p>
                  <p className="text-xs text-muted-foreground mt-1">Agrega una cuenta para recibir los pagos liberados</p>
                  <Button variant="outline" className="mt-4 gap-2" onClick={() => setShowNewBank(true)}>
                    <Plus className="w-4 h-4" /> Agregar cuenta bancaria
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="withdrawals" className="mt-4 space-y-4">
            {withdrawals.length > 0 ? withdrawals.map(w => (
              <Card key={w.id} className="glass-card">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center",
                        w.status === 'completed' ? "bg-emerald-500/10" : w.status === 'pending' ? "bg-amber-500/10" : "bg-red-500/10")}>
                        {w.status === 'completed' ? <CheckCircle2 className="w-5 h-5 text-emerald-700 dark:text-emerald-400" /> :
                         w.status === 'pending' ? <Clock className="w-5 h-5 text-amber-700 dark:text-amber-400" /> :
                         <XCircle className="w-5 h-5 text-red-600 dark:text-red-400" />}
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{fmt(w.amount)}</p>
                        <p className="text-xs text-muted-foreground">{w.bank_name} ({w.account_masked})</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className={cn("text-xs",
                        w.status === 'completed' ? "text-emerald-700 dark:text-emerald-400 border-emerald-500/30" :
                        w.status === 'pending' ? "text-amber-700 dark:text-amber-400 border-amber-500/30" :
                        "text-red-600 dark:text-red-400 border-red-500/30")}>
                        {w.status === 'completed' ? 'Completado' : w.status === 'pending' ? 'En proceso' : 'Fallido'}
                      </Badge>
                      <p className="text-xs text-muted-foreground mt-1">
                        {w.estimated_arrival && `Estimado: ${w.estimated_arrival}`}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )) : (
              <Card className="glass-card border-dashed">
                <CardContent className="py-10 text-center">
                  <ArrowUpRight className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                  <p className="text-muted-foreground">No hay retiros realizados</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* New Transaction Dialog */}
      <Dialog open={showNewTransaction} onOpenChange={setShowNewTransaction}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nueva Transacción Escrow</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-xs text-amber-600 dark:text-amber-700 dark:text-amber-400">
                <Shield className="w-3 h-3 inline mr-1" />
                Los fondos se retendrán hasta que confirmes la entrega del producto/servicio.
              </p>
            </div>
            <div>
              <Label>Cliente *</Label>
              <Select value={txForm.customer_id} onValueChange={v => setTxForm({...txForm, customer_id: v})}>
                <SelectTrigger data-testid="escrow-customer-select"><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Producto (opcional)</Label>
              <Select value={txForm.product_id} onValueChange={handleProductSelect}>
                <SelectTrigger data-testid="escrow-product-select"><SelectValue placeholder="Seleccionar producto" /></SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} {p.price ? `- $${p.price}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nombre del producto/servicio *</Label>
              <Input data-testid="escrow-product-name" value={txForm.product_name} onChange={e => setTxForm({...txForm, product_name: e.target.value})} placeholder="Ej: Consultoría Premium" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Monto *</Label>
                <Input data-testid="escrow-amount" type="number" value={txForm.amount} onChange={e => setTxForm({...txForm, amount: e.target.value})} placeholder="0.00" min="1" step="0.01" />
              </div>
              <div>
                <Label>Moneda</Label>
                <Select value={txForm.currency} onValueChange={v => setTxForm({...txForm, currency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usd">USD</SelectItem>
                    <SelectItem value="eur">EUR</SelectItem>
                    <SelectItem value="mxn">MXN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {txForm.amount && (
              <div className="p-3 rounded-lg bg-muted/50 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Monto:</span> <span>{fmt(parseFloat(txForm.amount || 0))}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Comisión (2.5%):</span> <span>{fmt(parseFloat(txForm.amount || 0) * 0.025)}</span></div>
                <div className="flex justify-between font-semibold border-t border-border pt-1 mt-1"><span>Neto a recibir:</span> <span className="text-emerald-700 dark:text-emerald-400">{fmt(parseFloat(txForm.amount || 0) * 0.975)}</span></div>
              </div>
            )}
            <Button data-testid="create-escrow-btn" className="w-full bg-primary" onClick={handleCreateTransaction} disabled={!txForm.customer_id || !txForm.product_name || !txForm.amount}>
              <Shield className="w-4 h-4 mr-2" /> Crear Pago Seguro
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Bank Account Dialog */}
      <Dialog open={showNewBank} onOpenChange={setShowNewBank}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Agregar Cuenta Bancaria</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <p className="text-xs text-emerald-600 dark:text-emerald-700 dark:text-emerald-400">
                <Lock className="w-3 h-3 inline mr-1" />
                Tus datos bancarios se almacenan de forma segura. Solo se guardan los últimos 4 dígitos.
              </p>
            </div>
            <div>
              <Label>Banco *</Label>
              <Input data-testid="bank-name-input" value={bankForm.bank_name} onChange={e => setBankForm({...bankForm, bank_name: e.target.value})} placeholder="Nombre del banco" />
            </div>
            <div>
              <Label>Titular de la cuenta *</Label>
              <Input data-testid="bank-holder-input" value={bankForm.account_holder} onChange={e => setBankForm({...bankForm, account_holder: e.target.value})} placeholder="Nombre completo" />
            </div>
            <div>
              <Label>Número de cuenta *</Label>
              <Input data-testid="bank-account-input" type="password" value={bankForm.account_number} onChange={e => setBankForm({...bankForm, account_number: e.target.value})} placeholder="Número de cuenta" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={bankForm.account_type} onValueChange={v => setBankForm({...bankForm, account_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checking">Corriente</SelectItem>
                    <SelectItem value="savings">Ahorros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Moneda</Label>
                <Select value={bankForm.currency} onValueChange={v => setBankForm({...bankForm, currency: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="usd">USD</SelectItem>
                    <SelectItem value="eur">EUR</SelectItem>
                    <SelectItem value="mxn">MXN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Número de ruta (opcional)</Label>
              <Input type="password" value={bankForm.routing_number} onChange={e => setBankForm({...bankForm, routing_number: e.target.value})} placeholder="Routing number" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Cuenta principal</Label>
              <Switch checked={bankForm.is_primary} onCheckedChange={checked => setBankForm({...bankForm, is_primary: checked})} />
            </div>
            <Button data-testid="save-bank-btn" className="w-full bg-primary" onClick={handleCreateBank} disabled={!bankForm.bank_name || !bankForm.account_holder || !bankForm.account_number}>
              <Building2 className="w-4 h-4 mr-2" /> Guardar Cuenta
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Privacy Policy Dialog */}
      <Dialog open={showPolicy} onOpenChange={setShowPolicy}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader><DialogTitle>Políticas de Seguridad y Privacidad</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            {policy && (
              <div className="space-y-6 py-2">
                <div>
                  <h3 className="text-lg font-bold mb-1">{policy.privacy_policy.title}</h3>
                  <p className="text-xs text-muted-foreground mb-4">Actualizado: {policy.privacy_policy.last_updated}</p>
                  {policy.privacy_policy.sections.map((s) => (
                    <div key={s.title} className="mb-4">
                      <h4 className="text-sm font-semibold mb-1">{s.title}</h4>
                      <p className="text-sm text-muted-foreground">{s.content}</p>
                    </div>
                  ))}
                </div>
                <div className="border-t border-border pt-4">
                  <h3 className="text-lg font-bold mb-3">{policy.terms_of_service.title}</h3>
                  <ul className="space-y-2">
                    {policy.terms_of_service.sections.map((s, i) => (
                      <li key={`tos-${String(s).slice(0,30)}-${i}`} className="text-sm text-muted-foreground flex gap-2">
                        <span className="text-primary font-bold">{i + 1}.</span> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Withdrawal Dialog */}
      <Dialog open={showWithdraw} onOpenChange={setShowWithdraw}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Solicitar Retiro de Fondos</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <p className="text-xs text-emerald-600 dark:text-emerald-700 dark:text-emerald-400">
                <ArrowUpRight className="w-3 h-3 inline mr-1" />
                Disponible para retiro: <strong>{fmt(balance?.available_to_withdraw)}</strong>
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                El retiro se procesará en 3-5 días hábiles.
              </p>
            </div>
            <div>
              <Label>Cuenta destino *</Label>
              <Select value={withdrawForm.bank_account_id} onValueChange={v => setWithdrawForm({...withdrawForm, bank_account_id: v})}>
                <SelectTrigger data-testid="withdraw-bank-select"><SelectValue placeholder="Seleccionar cuenta" /></SelectTrigger>
                <SelectContent>
                  {bankAccounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.bank_name} ({a.account_number_masked}) {a.is_primary ? '- Principal' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Monto a retirar *</Label>
              <Input
                data-testid="withdraw-amount"
                type="number"
                value={withdrawForm.amount}
                onChange={e => setWithdrawForm({...withdrawForm, amount: e.target.value})}
                placeholder="0.00"
                min="1"
                max={balance?.available_to_withdraw || 0}
                step="0.01"
              />
            </div>
            <Button
              data-testid="confirm-withdraw-btn"
              className="w-full bg-primary"
              onClick={handleWithdraw}
              disabled={!withdrawForm.bank_account_id || !withdrawForm.amount || parseFloat(withdrawForm.amount) > (balance?.available_to_withdraw || 0)}
            >
              <ArrowUpRight className="w-4 h-4 mr-2" /> Solicitar Retiro
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EscrowPayments;
